import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import apiClient from '@/utils/api';
import IntakeProgressCard from '@/components/IntakeProgressCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Users, TrendingUp, GraduationCap, Handshake, Ban, CalendarCheck, Download, FileSpreadsheet, Building2, PhoneOff, CalendarClock, AlertCircle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Design tokens from logo colors
const COLORS = {
  primary: '#192650',      // Navy from logo
  accent: '#0DA891',       // Teal from logo
  accentLight: '#E8F7F5',  // Light teal
  background: '#F8FAFC',   // Neutral gray
  cardBg: '#FFFFFF',
  textSecondary: '#64748B',
  border: '#E2E8F0',
};

const AdminDashboardUnified = () => {
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [adminSummary, setAdminSummary] = useState(null);
  const [teamsPerformance, setTeamsPerformance] = useState([]);
  const [expandedTeams, setExpandedTeams] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [availability, setAvailability] = useState({ summary: { total: 0, available: 0, unavailable: 0 }, counsellors: [] });
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  const fetchAvailability = async () => {
    try {
      setAvailabilityLoading(true);
      const res = await apiClient.get('/users/counsellors/availability');
      setAvailability(res.data || { summary: { total: 0, available: 0, unavailable: 0 }, counsellors: [] });
    } catch (e) {
      // Silent — admin-only endpoint
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const toggleCounsellorAvailability = async (c) => {
    try {
      await apiClient.put(`/users/${c.id}/availability`, { is_available: !c.is_available });
      toast.success(`${c.full_name} marked ${!c.is_available ? 'Available' : 'Unavailable'}`);
      fetchAvailability();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update availability');
    }
  };

  useEffect(() => {
    fetchAvailability();
    const t = setInterval(fetchAvailability, 60000);
    return () => clearInterval(t);
  }, []);

  const fetchDashboardData = async (isBackground = false) => {
    try {
      // Only show loading spinner on initial load, not on refresh
      if (!isBackground && !stats) {
        setLoading(true);
      }
      
      if (isBackground) {
        setIsRefreshing(true);
      }
      
      const [statsResponse, teamsResponse, summaryResponse] = await Promise.all([
        apiClient.get('/dashboard/unified-stats'),
        apiClient.get('/dashboard/teams-performance'),
        apiClient.get('/dashboard/admin-summary')
      ]);
      
      // Use functional updates to prevent unnecessary re-renders
      setStats(statsResponse.data);
      setTeamsPerformance(teamsResponse.data.teams || []);
      setAdminSummary(summaryResponse.data);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (!isBackground) {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'operating_head') {
      fetchDashboardData(false);
      
      // Silent background refresh every 60 seconds
      const interval = setInterval(() => {
        fetchDashboardData(true);
      }, 60000);
      
      return () => clearInterval(interval);
    }
  }, [user?.role]);
  
  if (user?.role !== 'admin' && user?.role !== 'operating_head') {
    return (
      <div className="text-center py-12" data-testid="access-denied">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: COLORS.accentLight }}>
          <i className="fas fa-lock text-2xl" style={{ color: COLORS.accent }}></i>
        </div>
        <h3 className="text-lg font-semibold mb-2" style={{ color: COLORS.primary }}>Access Denied</h3>
        <p style={{ color: COLORS.textSecondary }}>This dashboard is only accessible to administrators.</p>
      </div>
    );
  }

  const toggleTeamExpand = (teamId) => {
    setExpandedTeams(prev => ({
      ...prev,
      [teamId]: !prev[teamId]
    }));
  };

  // Only show loading skeleton on initial load when no data exists
  if (loading && !stats) {
    return (
      <div className="space-y-6 p-4 md:p-6" style={{ backgroundColor: COLORS.background, minHeight: '100vh' }}>
        <div className="h-16 rounded-xl animate-pulse" style={{ backgroundColor: COLORS.border }}></div>
        <div className="gap-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-16 rounded-xl animate-pulse" style={{ backgroundColor: COLORS.border }}></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 p-4 md:p-6 min-h-screen" style={{ backgroundColor: COLORS.background }} data-testid="admin-dashboard">
      {/* Subtle refresh indicator - doesn't cause layout shift */}
      {isRefreshing && (
        <div className="fixed top-2 right-2 z-50">
          <div className="h-2 w-2 rounded-full animate-pulse" style={{ backgroundColor: COLORS.accent }}></div>
        </div>
      )}
      
      {/* Compact Header */}
      <div 
        className="rounded-xl p-4 md:p-5 transition-all duration-300"
        style={{ backgroundColor: COLORS.primary }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg md:text-xl font-semibold text-white mb-0.5">Admin Dashboard</h1>
            <p className="text-sm text-white/70">Team & counselor performance overview</p>
          </div>
          <div 
            className="hidden md:flex h-12 w-12 rounded-xl items-center justify-center"
            style={{ backgroundColor: COLORS.accent }}
          >
            <TrendingUp className="h-6 w-6 text-white" />
          </div>
        </div>
      </div>

      {/* System Overview - Compact Cards in 2-column grid on mobile */}
      <section>
        <h2 className="text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: COLORS.primary }}>
          <Building2 className="h-4 w-4" style={{ color: COLORS.accent }} />
          System Overview
        </h2>
        <div 
          className="gap-2"
          style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
          }}
        >
          <CompactStatCard 
            icon={<Users className="h-3.5 w-3.5" />} 
            value={adminSummary?.summary_cards?.total_leads || stats?.core_kpis?.total_leads_assigned || 0} 
            label="Total Leads" 
            accentColor="#3B82F6"
            to="/leads"
          />
          <CompactStatCard 
            icon={<TrendingUp className="h-3.5 w-3.5" />} 
            value={adminSummary?.summary_cards?.active_leads || stats?.core_kpis?.active_leads || 0} 
            label="Active Leads" 
            accentColor="#06B6D4"
            to="/leads"
          />
          <CompactStatCard 
            icon={<AlertCircle className="h-3.5 w-3.5" />} 
            value={stats?.additional?.total_overdue || 0} 
            label="Overdue" 
            accentColor="#EF4444"
            to="/leads?tab=overdue"
          />
          <CompactStatCard 
            icon={<Handshake className="h-3.5 w-3.5" />} 
            value={adminSummary?.summary_cards?.total_prospects || stats?.core_kpis?.total_prospects || 0} 
            label="Prospects" 
            accentColor="#8B5CF6"
            to="/leads?prospective=1"
          />
          <CompactStatCard 
            icon={<PhoneOff className="h-3.5 w-3.5" />} 
            value={stats?.additional?.total_rnr || 0} 
            label="RNR" 
            accentColor="#F97316"
            to="/leads?tab=rnr"
          />
          <CompactStatCard 
            icon={<CalendarClock className="h-3.5 w-3.5" />} 
            value={stats?.core_kpis?.future_prospects || 0} 
            label="Future" 
            accentColor="#F59E0B"
            to="/leads?tab=future"
          />
          <CompactStatCard 
            icon={<GraduationCap className="h-3.5 w-3.5" />} 
            value={adminSummary?.summary_cards?.total_admissions || stats?.core_kpis?.total_admissions || 0} 
            label="Admissions" 
            accentColor={COLORS.accent}
            to="/admissions"
          />
          <CompactStatCard 
            icon={<Ban className="h-3.5 w-3.5" />} 
            value={adminSummary?.summary_cards?.bad_leads || stats?.core_kpis?.bad_leads || 0} 
            label="Bad Leads" 
            accentColor="#6B7280"
            to="/leads?tab=bad"
          />
        </div>
      </section>

      {/* Monthly Stats & Conversion Rate - Side by Side */}
      <section>
        <div 
          className="gap-2"
          style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
          }}
        >
          <CompactStatCard 
            icon={<CalendarCheck className="h-3.5 w-3.5" />} 
            value={adminSummary?.summary_cards?.this_month_admissions || 0} 
            label="This Month" 
            subLabel="Admissions"
            accentColor={COLORS.accent}
          />
          <Card className="border shadow-sm overflow-hidden" style={{ borderColor: COLORS.border }}>
            <CardContent className="p-2.5">
              <div className="flex items-center gap-2">
                <div 
                  className="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: COLORS.accentLight }}
                >
                  <TrendingUp className="h-4 w-4" style={{ color: COLORS.accent }} />
                </div>
                <div className="flex flex-col">
                  <span className="text-lg font-bold leading-tight" style={{ color: COLORS.primary }}>
                    {stats?.insights?.conversion_rate || 0}%
                  </span>
                  <span className="text-[10px]" style={{ color: COLORS.textSecondary }}>
                    Conversion
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Counsellor Availability */}
      <section>
        <Card className="border shadow-sm overflow-hidden" style={{ borderColor: COLORS.border }} data-testid="counsellor-availability-card">
          <CardHeader className="pb-3 px-4 pt-4" style={{ backgroundColor: COLORS.cardBg }}>
            <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm md:text-base">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" style={{ color: COLORS.accent }} />
                <span style={{ color: COLORS.primary }}>Counsellor Availability</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <Badge className="bg-green-100 text-green-700 border border-green-200 font-semibold" data-testid="avail-summary-available">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5"></span>
                  {availability.summary.available} Available
                </Badge>
                <Badge className="bg-red-100 text-red-700 border border-red-200 font-semibold" data-testid="avail-summary-unavailable">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 mr-1.5"></span>
                  {availability.summary.unavailable} Unavailable
                </Badge>
              </div>
            </CardTitle>
            <CardDescription className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
              Live status for new lead assignments. Round-robin auto-skips unavailable counsellors.
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            {availability.counsellors.length === 0 ? (
              <div className="py-6 text-center text-gray-400 text-sm">
                {availabilityLoading ? <><i className="fas fa-spinner fa-spin mr-2"></i>Loading...</> : 'No counsellors found.'}
              </div>
            ) : (
              <>
                {/* Mobile: stacked cards (no horizontal scroll) */}
                <div className="sm:hidden space-y-2" data-testid="avail-cards">
                  {availability.counsellors.map((c) => (
                    <div key={c.id} className={`rounded-lg border p-3 ${!c.is_available ? 'bg-red-50/40 border-red-200' : 'bg-white'}`} style={!c.is_available ? undefined : { borderColor: COLORS.border }} data-testid={`avail-card-${c.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-sm truncate" style={{ color: COLORS.primary }} title={c.full_name}>{c.full_name}</div>
                          <div className="text-[10px] text-gray-500 truncate" title={c.email}>{c.email}</div>
                        </div>
                        {/* Single toggle switch — shows state AND flips on tap */}
                        <button
                          onClick={() => toggleCounsellorAvailability(c)}
                          role="switch"
                          aria-checked={c.is_available}
                          className={`inline-flex items-center gap-2 pl-2.5 pr-1.5 py-1 rounded-full border text-xs font-semibold shrink-0 transition-colors ${c.is_available ? 'border-green-300 bg-green-50 text-green-700' : 'border-red-300 bg-red-50 text-red-700'}`}
                          data-testid={`avail-toggle-mobile-${c.id}`}
                        >
                          {c.is_available ? 'Available' : 'Unavailable'}
                          <span className={`relative w-9 h-5 rounded-full transition-colors ${c.is_available ? 'bg-green-500' : 'bg-red-400'}`}>
                            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${c.is_available ? 'left-[18px]' : 'left-0.5'}`}></span>
                          </span>
                        </button>
                      </div>
                      <div className="mt-2 pt-2 border-t" style={{ borderColor: COLORS.border }}>
                        <span className="text-xs text-gray-600">
                          <i className="fas fa-user-friends mr-1 text-gray-400"></i>
                          <b style={{ color: COLORS.primary }}>{c.active_leads}</b> active leads
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop: table */}
                <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm" data-testid="avail-table">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-[10px] uppercase tracking-wide" style={{ color: COLORS.textSecondary }}>
                      <th className="px-3 py-2 font-semibold">Counsellor</th>
                      <th className="px-3 py-2 font-semibold text-center">Availability</th>
                      <th className="px-3 py-2 font-semibold text-center">Active Leads</th>
                      <th className="px-3 py-2 font-semibold hidden md:table-cell">Last Toggled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availability.counsellors.map((c) => (
                      <tr key={c.id} className={`border-t hover:bg-gray-50 transition-colors ${!c.is_available ? 'bg-red-50/30' : ''}`} data-testid={`avail-row-${c.id}`}>
                        <td className="px-3 py-2.5">
                          <div className="font-semibold truncate max-w-[180px]" style={{ color: COLORS.primary }} title={c.full_name}>{c.full_name}</div>
                          <div className="text-[10px] text-gray-500 truncate max-w-[180px]" title={c.email}>{c.email} · {c.role}</div>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <button
                            onClick={() => toggleCounsellorAvailability(c)}
                            role="switch"
                            aria-checked={c.is_available}
                            className={`inline-flex items-center gap-2 pl-2.5 pr-1.5 py-1 rounded-full border text-xs font-semibold transition-colors ${c.is_available ? 'border-green-300 bg-green-50 text-green-700' : 'border-red-300 bg-red-50 text-red-700'}`}
                            data-testid={`avail-toggle-${c.id}`}
                          >
                            {c.is_available ? 'Available' : 'Unavailable'}
                            <span className={`relative w-9 h-5 rounded-full transition-colors ${c.is_available ? 'bg-green-500' : 'bg-red-400'}`}>
                              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${c.is_available ? 'left-[18px]' : 'left-0.5'}`}></span>
                            </span>
                          </button>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <span className={`font-bold text-base ${c.active_leads > 0 ? '' : 'text-gray-400'}`} style={c.active_leads > 0 ? { color: COLORS.primary } : undefined}>{c.active_leads}</span>
                        </td>
                        <td className="px-3 py-2.5 hidden md:table-cell text-xs text-gray-600">
                          {c.last_availability_change ? new Date(c.last_availability_change).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : <span className="text-gray-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Company admission target + intake countdown — placed under availability */}
      <IntakeProgressCard showMine={false} />

      {/* Intake Performance - intake-selectable & mode-aware (replaces the old overall Team Performance table) */}
      <IntakePerformanceCard />

      {/* Lifetime funnel — monthly conversion trend (survives bad-lead purges) */}
      <ConversionTrendCard />
    </div>
  );
};

// Lifetime funnel trend — plots monthly conversion snapshots over time.
const ConversionTrendCard = () => {
  const [snaps, setSnaps] = useState(null);
  const [capturing, setCapturing] = useState(false);

  const load = async () => {
    try {
      const res = await apiClient.get('/dashboard/conversion-snapshots');
      setSnaps(res.data?.snapshots || []);
    } catch (e) {
      setSnaps([]);
    }
  };
  useEffect(() => { load(); }, []);

  const capture = async () => {
    try {
      setCapturing(true);
      await apiClient.post('/dashboard/conversion-snapshots/capture');
      toast.success('Snapshot captured for this month');
      await load();
    } catch (e) {
      toast.error('Could not capture snapshot');
    } finally {
      setCapturing(false);
    }
  };

  const data = (snaps || []).map((s) => ({
    label: s.label || s.month,
    conversion: s.conversion_rate ?? 0,
    leads: s.total_leads ?? 0,
    admissions: s.admissions ?? 0,
  }));
  const latest = data[data.length - 1];

  return (
    <section data-testid="conversion-trend-card" className="mt-6">
      <Card className="border shadow-sm overflow-hidden" style={{ borderColor: COLORS.border }}>
        <CardHeader className="pb-3 px-4 pt-4" style={{ backgroundColor: COLORS.cardBg }}>
          <CardTitle className="flex items-center justify-between gap-2 text-sm md:text-base">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" style={{ color: COLORS.accent }} />
              <span style={{ color: COLORS.primary }}>Conversion Trend</span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={capture}
              disabled={capturing}
              className="h-8 text-xs"
              data-testid="capture-snapshot-btn"
            >
              <i className={`fas ${capturing ? 'fa-spinner fa-spin' : 'fa-camera'} mr-1.5`}></i>
              Snapshot now
            </Button>
          </CardTitle>
          <CardDescription className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
            Monthly lead→admission conversion · preserved across bad-lead cleanups
          </CardDescription>
        </CardHeader>
        <CardContent className="px-2 pb-4 pt-0 sm:px-4">
          {snaps === null ? (
            <div className="py-8 text-center text-sm" style={{ color: COLORS.textSecondary }}>
              <i className="fas fa-spinner fa-spin mr-2"></i>Loading…
            </div>
          ) : data.length === 0 ? (
            <div className="py-8 text-center text-sm" style={{ color: COLORS.textSecondary }} data-testid="conversion-trend-empty">
              No snapshots yet. Tap "Snapshot now" to record this month, then it updates automatically each month.
            </div>
          ) : (
            <>
              {latest && (
                <div className="flex items-baseline gap-2 px-2 pt-3 pb-1">
                  <span className="text-2xl font-bold" style={{ color: COLORS.accent }} data-testid="conversion-trend-latest">{latest.conversion}%</span>
                  <span className="text-xs" style={{ color: COLORS.textSecondary }}>{latest.label} · {latest.admissions} admissions / {latest.leads.toLocaleString()} leads</span>
                </div>
              )}
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: COLORS.textSecondary }} />
                  <YAxis tick={{ fontSize: 11, fill: COLORS.textSecondary }} unit="%" width={44} />
                  <Tooltip
                    formatter={(v, name) => name === 'conversion' ? [`${v}%`, 'Conversion'] : [v, name]}
                    contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: COLORS.border }}
                  />
                  <Line type="monotone" dataKey="conversion" stroke={COLORS.accent} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </>
          )}
        </CardContent>
      </Card>
    </section>
  );
};

// Intake Performance Card - intake-selectable & mode-aware (team vs country)
const IntakePerformanceCard = () => {
  const [intakes, setIntakes] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [viewMode, setViewMode] = useState('team');

  const load = async (intakeId, modeArg) => {
    try {
      setLoading(true);
      const params = {};
      if (intakeId) params.intake_id = intakeId;
      if (modeArg) params.mode = modeArg;
      const res = await apiClient.get('/dashboard/intake-performance', { params });
      const d = res.data || {};
      setData(d);
      if (Array.isArray(d.intakes)) setIntakes(d.intakes);
      if (d.intake?.id) setSelectedId(d.intake.id);
      if (d.mode) setViewMode(d.mode);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSelect = (id) => { setSelectedId(id); setExpanded({}); load(id, viewMode); };
  const onToggleMode = (m) => { if (m === viewMode) return; setViewMode(m); setExpanded({}); load(selectedId, m); };

  const mode = data?.mode || 'team';
  const rows = data?.rows || [];
  const total = rows.reduce((s, r) => s + (r.total || 0), 0);

  return (
    <section data-testid="intake-performance-card">
      <Card className="border shadow-sm overflow-hidden" style={{ borderColor: COLORS.border }}>
        <CardHeader className="pb-3 px-4 pt-4" style={{ backgroundColor: COLORS.cardBg }}>
          <CardTitle className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm md:text-base">
            <div className="flex items-center gap-2">
              <CalendarCheck className="h-4 w-4" style={{ color: COLORS.accent }} />
              <span style={{ color: COLORS.primary }}>Intake Performance</span>
            </div>
            <div className="relative w-full sm:w-auto">
              <select
                value={selectedId}
                onChange={(e) => onSelect(e.target.value)}
                className="w-full sm:w-56 max-w-full truncate text-xs font-medium pl-3 pr-9 py-2 rounded-lg border cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-200 bg-white appearance-none"
                style={{ borderColor: COLORS.border, color: COLORS.primary }}
                data-testid="intake-performance-select"
              >
                {intakes.length === 0 && <option value="">No intakes</option>}
                {intakes.map((it) => (
                  <option key={it.id} value={it.id}>
                    {it.name}{it.status ? ` · ${it.status}` : ''}
                  </option>
                ))}
              </select>
              <ChevronDown className="h-4 w-4 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: COLORS.textSecondary }} />
            </div>
          </CardTitle>
          {/* Team / Country segmented toggle — full-width on mobile, inline on desktop */}
          <div
            className="mt-3 inline-flex w-full sm:w-auto gap-1 rounded-xl border p-1"
            style={{ borderColor: COLORS.border, backgroundColor: '#F8FAFC' }}
            data-testid="intake-mode-toggle"
          >
            {[{ k: 'team', label: 'Team-wise', Icon: Users }, { k: 'country', label: 'Country-wise', Icon: Building2 }].map(({ k, label, Icon }) => {
              const active = viewMode === k;
              return (
                <button
                  key={k}
                  onClick={() => onToggleMode(k)}
                  data-testid={`intake-mode-${k}`}
                  className={`inline-flex flex-1 sm:flex-none items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold transition-all ${active ? 'shadow-sm' : ''}`}
                  style={active
                    ? { backgroundColor: COLORS.accent, color: '#fff' }
                    : { backgroundColor: 'transparent', color: COLORS.textSecondary }}
                >
                  <Icon className="h-3.5 w-3.5" />{label}
                </button>
              );
            })}
          </div>
          <CardDescription className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
            Admissions (active + flown) for the selected intake · {total} total
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0">
          {/* Leads → Admissions for this intake's marketing period (leads counted by
              the intake's start–end window; admissions = tagged, non-cancelled). */}
          {data?.summary && (
            <div className="grid grid-cols-3 gap-2 my-3" data-testid="intake-conversion-summary">
              <div className="rounded-lg border p-2.5 text-center" style={{ borderColor: COLORS.border, backgroundColor: '#F8FAFC' }}>
                <p className="text-lg font-bold" style={{ color: COLORS.primary }} data-testid="intake-leads-in-window">{(data.summary.leads_in_window || 0).toLocaleString()}</p>
                <p className="text-[10px] mt-0.5" style={{ color: COLORS.textSecondary }}>Leads (period)</p>
              </div>
              <div className="rounded-lg border p-2.5 text-center" style={{ borderColor: COLORS.border, backgroundColor: '#F0FDFA' }}>
                <p className="text-lg font-bold" style={{ color: COLORS.accent }} data-testid="intake-admissions-total">{data.summary.admissions || 0}</p>
                <p className="text-[10px] mt-0.5" style={{ color: COLORS.textSecondary }}>Admissions</p>
              </div>
              <div className="rounded-lg border p-2.5 text-center" style={{ borderColor: COLORS.border, backgroundColor: '#FEFCE8' }}>
                <p className="text-lg font-bold" style={{ color: '#B45309' }} data-testid="intake-conversion-rate">{data.summary.conversion_rate || 0}%</p>
                <p className="text-[10px] mt-0.5" style={{ color: COLORS.textSecondary }}>Conversion</p>
              </div>
            </div>
          )}
          {data?.summary?.start_date && (
            <p className="text-[10px] mb-2 -mt-1" style={{ color: COLORS.textSecondary }}>
              Marketing period: {data.summary.start_date} → {data.summary.end_date}
            </p>
          )}
          {loading ? (
            <div className="py-8 text-center text-sm" style={{ color: COLORS.textSecondary }}>
              <i className="fas fa-spinner fa-spin mr-2"></i>Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="py-8 text-center text-sm" style={{ color: COLORS.textSecondary }}>
              No admissions recorded for this intake yet
            </div>
          ) : mode === 'country' ? (
            <div className="space-y-2">
              {rows.map((c) => {
                const open = expanded[c.country];
                return (
                  <div key={c.country} className="border rounded-lg overflow-hidden" style={{ borderColor: COLORS.border }} data-testid={`intake-country-${c.country}`}>
                    <div
                      className="flex items-center justify-between p-3 cursor-pointer"
                      style={{ backgroundColor: open ? '#EEF2FF' : COLORS.cardBg }}
                      onClick={() => setExpanded((p) => ({ ...p, [c.country]: !open }))}
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" style={{ color: '#4F46E5' }} />
                        <span className="font-semibold text-sm" style={{ color: COLORS.primary }}>{c.country}</span>
                        <Badge className="text-[10px]" style={{ backgroundColor: '#EEF2FF', color: '#4F46E5' }}>{c.total}</Badge>
                      </div>
                      {open ? <ChevronUp className="h-4 w-4" style={{ color: COLORS.textSecondary }} /> : <ChevronDown className="h-4 w-4" style={{ color: COLORS.textSecondary }} />}
                    </div>
                    {open && (
                      <div className="divide-y" style={{ borderColor: COLORS.border }}>
                        {(c.counselors || []).map((cc, i) => (
                          <div key={cc.counselor_name + i} className="flex items-center justify-between px-4 py-2 text-sm" style={{ color: COLORS.primary }}>
                            <span>{cc.counselor_name}</span>
                            <span className="font-semibold">{cc.admissions}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-2">
              {rows.map((t) => {
                const key = t.team_id || 'no_team';
                const open = expanded[key];
                return (
                  <div key={key} className="border rounded-lg overflow-hidden" style={{ borderColor: COLORS.border }} data-testid={`intake-team-${t.team_id || 'no_team'}`}>
                    <div
                      className="flex items-center justify-between p-3 cursor-pointer"
                      style={{ backgroundColor: open ? '#ECFDF5' : COLORS.cardBg }}
                      onClick={() => setExpanded((p) => ({ ...p, [key]: !open }))}
                      data-testid={`intake-team-toggle-${t.team_id || 'no_team'}`}
                    >
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" style={{ color: COLORS.accent }} />
                        <span className="font-semibold text-sm" style={{ color: COLORS.primary }}>{t.team_name}</span>
                        <Badge className="text-[11px]" style={{ backgroundColor: COLORS.accentLight, color: COLORS.accent }}>{t.total}</Badge>
                      </div>
                      {open ? <ChevronUp className="h-4 w-4" style={{ color: COLORS.textSecondary }} /> : <ChevronDown className="h-4 w-4" style={{ color: COLORS.textSecondary }} />}
                    </div>
                    {open && (
                      <div className="divide-y" style={{ borderColor: COLORS.border }}>
                        {(t.counselors || []).length === 0 ? (
                          <div className="px-4 py-2 text-xs" style={{ color: COLORS.textSecondary }}>No members</div>
                        ) : (t.counselors || []).map((cc, i) => (
                          <div key={cc.counselor_name + i} className="flex items-center justify-between px-4 py-2 text-sm" style={{ color: COLORS.primary }} data-testid={`intake-team-member-${t.team_id || 'no_team'}-${i}`}>
                            <span>{cc.counselor_name}</span>
                            <span className="font-semibold">{cc.admissions}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
};

// Compact Stat Card Component - Clean Design
const CompactStatCard = ({ icon, value, label, subLabel, accentColor, to }) => {
  const navigate = useNavigate();
  const clickable = !!to;
  return (
    <Card 
      className={`border shadow-sm transition-all ${clickable ? 'cursor-pointer hover:shadow-md active:scale-[0.98] hover:-translate-y-0.5' : 'cursor-default'}`}
      style={{ borderColor: COLORS.border }}
      onClick={clickable ? () => navigate(to) : undefined}
      role={clickable ? 'button' : undefined}
      data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-2.5">
        <div className="flex items-center gap-2">
          <div 
            className="h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${accentColor}15` }}
          >
            <span style={{ color: accentColor }}>{icon}</span>
          </div>
          <div className="flex items-baseline gap-1.5 min-w-0">
            <span className="text-lg font-bold" style={{ color: COLORS.primary }}>{value.toLocaleString()}</span>
            <span className="text-[10px] truncate" style={{ color: COLORS.textSecondary }}>
              {label}{subLabel && <span className="text-[9px]" style={{ color: COLORS.accent }}> {subLabel}</span>}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Team Accordion Component - Clean Design with Smooth Animation
const TeamAccordion = ({ team, isExpanded, onToggle }) => {
  const stats = team.team_stats;
  const targetProgress = stats.target_progress || 0;
  const isTargetMet = stats.monthly_admissions >= stats.monthly_target;
  
  return (
    <div 
      className="border rounded-xl overflow-hidden transition-all duration-300 md:border-l-[3px]"
      style={{ 
        borderColor: COLORS.border,
        borderLeftColor: team.team_color || COLORS.accent
      }}
      data-testid={`team-accordion-${team.team_id}`}
    >
      {/* Team Header - Clickable */}
      <div 
        className="p-3 md:p-4 cursor-pointer transition-colors duration-200"
        style={{ backgroundColor: isExpanded ? COLORS.accentLight : COLORS.cardBg }}
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-9 h-9 md:w-10 md:h-10 rounded-lg flex items-center justify-center text-white font-semibold text-sm"
              style={{ backgroundColor: team.team_color || COLORS.accent }}
            >
              {team.team_name.charAt(0)}
            </div>
            <div>
              <h3 className="font-semibold text-sm md:text-base" style={{ color: COLORS.primary }}>{team.team_name}</h3>
              <div className="flex items-center gap-2 text-xs" style={{ color: COLORS.textSecondary }}>
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {team.member_count}
                </span>
                {team.team_leader && (
                  <span className="flex items-center gap-1 text-amber-600">
                    <span>👑</span>
                    {team.team_leader}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3 md:gap-4">
            {/* Desktop Stats Summary */}
            <div className="hidden md:flex items-center gap-4 text-sm">
              <div className="text-center">
                <div className="font-bold" style={{ color: COLORS.primary }}>{stats.total_leads}</div>
                <div className="text-[10px]" style={{ color: COLORS.textSecondary }}>Leads</div>
              </div>
              <div className="text-center">
                <div className="font-bold" style={{ color: COLORS.accent }}>{stats.admissions}</div>
                <div className="text-[10px]" style={{ color: COLORS.textSecondary }}>Admissions</div>
              </div>
              {/* Target Progress Bar */}
              <div className="w-28">
                <div className="flex justify-between text-[10px] mb-1">
                  <span style={{ color: COLORS.textSecondary }}>Target</span>
                  <span className="font-semibold" style={{ color: isTargetMet ? COLORS.accent : COLORS.primary }}>
                    {stats.monthly_admissions}/{stats.monthly_target}
                  </span>
                </div>
                <div className="w-full rounded-full h-1.5" style={{ backgroundColor: COLORS.border }}>
                  <div
                    className="h-1.5 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${Math.min(targetProgress, 100)}%`,
                      backgroundColor: isTargetMet ? COLORS.accent : targetProgress >= 50 ? '#F59E0B' : '#EF4444'
                    }}
                  />
                </div>
              </div>
            </div>
            
            {/* Expand Icon */}
            <div 
              className="h-7 w-7 rounded-lg flex items-center justify-center transition-all duration-300"
              style={{ 
                backgroundColor: isExpanded ? COLORS.accent : COLORS.border,
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            >
              <ChevronDown className={`h-4 w-4 ${isExpanded ? 'text-white' : ''}`} style={{ color: isExpanded ? 'white' : COLORS.textSecondary }} />
            </div>
          </div>
        </div>
        
        {/* Mobile Stats Row */}
        <div className="md:hidden mt-3 pt-3" style={{ borderTopWidth: '1px', borderTopColor: COLORS.border }}>
          <div className="flex justify-around text-center text-xs mb-2">
            <div>
              <span className="font-bold" style={{ color: COLORS.primary }}>{stats.total_leads}</span>
              <span style={{ color: COLORS.textSecondary }}> Leads</span>
            </div>
            <div>
              <span className="font-bold" style={{ color: COLORS.accent }}>{stats.admissions}</span>
              <span style={{ color: COLORS.textSecondary }}> Admits</span>
            </div>
          </div>
          {/* Mobile Target Progress */}
          <div>
            <div className="flex justify-between text-[10px] mb-1">
              <span style={{ color: COLORS.textSecondary }}>Target (5×{team.member_count})</span>
              <span className="font-semibold" style={{ color: isTargetMet ? COLORS.accent : COLORS.primary }}>
                {stats.monthly_admissions}/{stats.monthly_target}
              </span>
            </div>
            <div className="w-full rounded-full h-1.5" style={{ backgroundColor: COLORS.border }}>
              <div
                className="h-1.5 rounded-full transition-all duration-500"
                style={{ 
                  width: `${Math.min(targetProgress, 100)}%`,
                  backgroundColor: isTargetMet ? COLORS.accent : targetProgress >= 50 ? '#F59E0B' : '#EF4444'
                }}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Expanded Content with Animation */}
      <div 
        className={`transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}
        style={{ borderTopWidth: isExpanded ? '1px' : '0', borderTopColor: COLORS.border }}
      >
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto" style={{ backgroundColor: COLORS.cardBg }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: `${COLORS.accent}08` }}>
                <th className="text-left py-2.5 px-4 font-medium text-xs" style={{ color: COLORS.textSecondary }}>Counselor</th>
                <th className="text-center py-2.5 px-2 font-medium text-xs" style={{ color: COLORS.textSecondary }}>Total</th>
                <th className="text-center py-2.5 px-2 font-medium text-xs" style={{ color: COLORS.textSecondary }}>Active</th>
                <th className="text-center py-2.5 px-2 font-medium text-xs" style={{ color: COLORS.textSecondary }}>Prospects</th>
                <th className="text-center py-2.5 px-2 font-medium text-xs" style={{ color: COLORS.textSecondary }}>Admissions</th>
                <th className="text-center py-2.5 px-2 font-medium text-xs" style={{ color: COLORS.textSecondary }}>Monthly</th>
                <th className="text-center py-2.5 px-2 font-medium text-xs" style={{ color: COLORS.textSecondary }}>RNR</th>
                <th className="text-center py-2.5 px-2 font-medium text-xs" style={{ color: COLORS.textSecondary }}>Conv %</th>
              </tr>
            </thead>
            <tbody>
              {team.counselors.map((counselor) => (
                <tr 
                  key={counselor.counselor_id} 
                  className="transition-colors duration-150 hover:bg-gray-50"
                  style={{ borderTopWidth: '1px', borderTopColor: COLORS.border }}
                >
                  <td className="py-2.5 px-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm" style={{ color: COLORS.primary }}>{counselor.counselor_name}</span>
                      {counselor.is_leader && (
                        <Badge className="text-[9px] px-1.5" style={{ backgroundColor: '#FEF3C7', color: '#D97706' }}>
                          Leader
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="text-center py-2.5 px-2 font-semibold text-sm" style={{ color: COLORS.primary }}>{counselor.total_leads}</td>
                  <td className="text-center py-2.5 px-2 text-sm" style={{ color: '#06B6D4' }}>{counselor.active_leads}</td>
                  <td className="text-center py-2.5 px-2 text-sm" style={{ color: '#8B5CF6' }}>{counselor.prospects}</td>
                  <td className="text-center py-2.5 px-2 font-semibold text-sm" style={{ color: COLORS.accent }}>{counselor.admissions}</td>
                  <td className="text-center py-2.5 px-2 text-sm">
                    <span style={{ color: counselor.monthly_admissions > 0 ? COLORS.accent : COLORS.textSecondary, fontWeight: counselor.monthly_admissions > 0 ? '600' : '400' }}>
                      {counselor.monthly_admissions}/{counselor.monthly_target}
                    </span>
                  </td>
                  <td className="text-center py-2.5 px-2">
                    {counselor.rnr > 0 ? (
                      <Badge variant="outline" className="text-xs" style={{ backgroundColor: '#FEF3C7', color: '#D97706', borderColor: '#FCD34D' }}>
                        {counselor.rnr}
                      </Badge>
                    ) : (
                      <span style={{ color: COLORS.border }}>0</span>
                    )}
                  </td>
                  <td className="text-center py-2.5 px-2 font-medium text-sm" style={{ color: COLORS.accent }}>{counselor.conversion_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Mobile Cards - Simplified Clean Design */}
        <div className="md:hidden p-2 space-y-2" style={{ backgroundColor: COLORS.cardBg }}>
          {team.counselors.map((counselor) => (
            <div 
              key={counselor.counselor_id} 
              className="p-3 rounded-lg border"
              style={{ borderColor: COLORS.border, backgroundColor: COLORS.cardBg }}
            >
              {/* Header with name, leader badge, and conversion */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm" style={{ color: COLORS.primary }}>{counselor.counselor_name}</span>
                  {counselor.is_leader && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#FEF3C7', color: '#D97706' }}>
                      👑
                    </span>
                  )}
                </div>
                <span 
                  className="text-xs font-medium"
                  style={{ color: counselor.conversion_rate >= 10 ? COLORS.accent : COLORS.textSecondary }}
                >
                  {counselor.conversion_rate}%
                </span>
              </div>
              
              {/* Simplified Stats - Single Row */}
              <div className="flex items-center justify-between text-xs" style={{ color: COLORS.textSecondary }}>
                <div className="flex items-center gap-3">
                  <span><strong style={{ color: COLORS.primary }}>{counselor.total_leads}</strong> leads</span>
                  <span><strong style={{ color: COLORS.accent }}>{counselor.admissions}</strong> admits</span>
                  <span><strong style={{ color: counselor.monthly_admissions >= 5 ? COLORS.accent : '#D97706' }}>{counselor.monthly_admissions}/5</strong> monthly</span>
                </div>
                {counselor.rnr > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#FEF3C7', color: '#D97706' }}>
                    {counselor.rnr} RNR
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Daily Report Download Component - Clean Design
const DailyReportCard = ({ user, teams }) => {
  const [dailyReportDate, setDailyReportDate] = useState(() => new Date().toLocaleDateString('en-CA'));
  const [reportType, setReportType] = useState('all');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedCounselor, setSelectedCounselor] = useState('');
  const [counselorsList, setCounselorsList] = useState([]);
  const [downloadingDaily, setDownloadingDaily] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'operating_head') {
      fetchCounselors();
    }
  }, [user]);

  const fetchCounselors = async () => {
    try {
      const response = await apiClient.get('/users');
      const counselors = response.data.filter(u =>
        u.role === 'counselor' || u.role === 'team_leader' ||
        (u.role === 'operating_head' && u.also_counselor)
      );
      setCounselorsList(counselors);
    } catch (error) {
      console.error('Error fetching counselors:', error);
    }
  };

  const handleDailyReportDownload = async () => {
    if (!dailyReportDate) {
      toast.error('Please select a date');
      return;
    }

    setDownloadingDaily(true);

    try {
      const params = new URLSearchParams({
        date: dailyReportDate,
        format: 'excel'
      });

      if (reportType === 'team' && selectedTeam) {
        params.append('team_id', selectedTeam);
      } else if (reportType === 'counselor' && selectedCounselor) {
        params.append('counselor_id', selectedCounselor);
      }

      const response = await apiClient.get(`/reports/daily-report?${params.toString()}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      let filename = `Daily_Report_${dailyReportDate}.xlsx`;
      if (reportType === 'team' && selectedTeam) {
        const team = teams.find(t => t.team_id === selectedTeam);
        filename = `Daily_Report_${team?.team_name || 'Team'}_${dailyReportDate}.xlsx`;
      } else if (reportType === 'counselor' && selectedCounselor) {
        const counselor = counselorsList.find(c => c.id === selectedCounselor);
        filename = `Daily_Report_${counselor?.full_name || 'Counselor'}_${dailyReportDate}.xlsx`;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Daily report downloaded successfully');
    } catch (error) {
      console.error('Error downloading daily report:', error);
      toast.error(`Failed to download report: ${error.response?.status || error.message}`);
    } finally {
      setDownloadingDaily(false);
    }
  };

  const inputStyles = {
    width: '100%',
    padding: '8px 12px',
    border: `1px solid ${COLORS.border}`,
    borderRadius: '8px',
    fontSize: '14px',
    color: COLORS.primary,
    backgroundColor: COLORS.cardBg,
    transition: 'border-color 0.2s, box-shadow 0.2s',
    outline: 'none',
    textAlign: 'left',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    appearance: 'none',
  };

  return (
    <Card className="border shadow-sm overflow-hidden" style={{ borderColor: COLORS.border }} data-testid="daily-report-card">
      <CardHeader className="pb-3 p-4" style={{ backgroundColor: COLORS.accentLight }}>
        <CardTitle className="flex items-center gap-2 text-sm md:text-base">
          <FileSpreadsheet className="h-4 w-4 md:h-5 md:w-5" style={{ color: COLORS.accent }} />
          <span style={{ color: COLORS.primary }}>Daily Activity Report</span>
        </CardTitle>
        <CardDescription className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
          Download reports for teams, counselors, or all data
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4 space-y-4 p-4">
        {/* Date Selection */}
        <div>
          <label className="text-sm font-medium block mb-1.5" style={{ color: COLORS.primary }}>Select Date</label>
          <input
            type="date"
            className="w-full text-left"
            style={inputStyles}
            value={dailyReportDate}
            onChange={(e) => setDailyReportDate(e.target.value)}
            max={new Date().toLocaleDateString('en-CA')}
            onFocus={(e) => {
              e.target.style.borderColor = COLORS.accent;
              e.target.style.boxShadow = `0 0 0 3px ${COLORS.accent}20`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = COLORS.border;
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        {/* Report Type Selection */}
        <div>
          <label className="text-sm font-medium block mb-1.5" style={{ color: COLORS.primary }}>Report Type</label>
          <select
            style={inputStyles}
            value={reportType}
            onChange={(e) => {
              setReportType(e.target.value);
              setSelectedTeam('');
              setSelectedCounselor('');
            }}
            onFocus={(e) => {
              e.target.style.borderColor = COLORS.accent;
              e.target.style.boxShadow = `0 0 0 3px ${COLORS.accent}20`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = COLORS.border;
              e.target.style.boxShadow = 'none';
            }}
          >
            <option value="all">All Counselors</option>
            <option value="team">By Team</option>
            <option value="counselor">By Counselor</option>
          </select>
        </div>

        {/* Team Selection */}
        {reportType === 'team' && (
          <div>
            <label className="text-sm font-medium block mb-1.5" style={{ color: COLORS.primary }}>Select Team</label>
            <select
              style={inputStyles}
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              onFocus={(e) => {
                e.target.style.borderColor = COLORS.accent;
                e.target.style.boxShadow = `0 0 0 3px ${COLORS.accent}20`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = COLORS.border;
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="">Choose a team...</option>
              {teams.map((team) => (
                <option key={team.team_id} value={team.team_id}>
                  {team.team_name} ({team.member_count} members)
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Counselor Selection */}
        {reportType === 'counselor' && (
          <div>
            <label className="text-sm font-medium block mb-1.5" style={{ color: COLORS.primary }}>Select Counselor</label>
            <select
              style={inputStyles}
              value={selectedCounselor}
              onChange={(e) => setSelectedCounselor(e.target.value)}
              onFocus={(e) => {
                e.target.style.borderColor = COLORS.accent;
                e.target.style.boxShadow = `0 0 0 3px ${COLORS.accent}20`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = COLORS.border;
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="">Choose a counselor...</option>
              {counselorsList.map((counselor) => (
                <option key={counselor.id} value={counselor.id}>
                  {counselor.full_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Download Button */}
        <Button
          onClick={handleDailyReportDownload}
          disabled={downloadingDaily || !dailyReportDate || (reportType === 'team' && !selectedTeam) || (reportType === 'counselor' && !selectedCounselor)}
          className="w-full transition-all duration-200 hover:opacity-90"
          style={{ 
            backgroundColor: COLORS.accent, 
            color: 'white',
            opacity: (downloadingDaily || !dailyReportDate || (reportType === 'team' && !selectedTeam) || (reportType === 'counselor' && !selectedCounselor)) ? 0.5 : 1
          }}
          data-testid="download-report-btn"
        >
          <Download className="h-4 w-4 mr-2" />
          {downloadingDaily ? 'Downloading...' : 'Download Report'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default AdminDashboardUnified;
